import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { geolocationService } from '@/services/geolocation';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('workplus_token'));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem('workplus_token');
    setToken(null);
    setUser(null);
    setCompany(null);
    geolocationService.stopTracking();
  }, []);

  const loadCompany = useCallback(async () => {
    try {
      const response = await api.get('/companies/my');
      setCompany(response.data);
    } catch (error) {
      console.log('No company associated with user');
    }
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
          if (response.data.company_id) {
            loadCompany();
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token, logout, loadCompany]);

  useEffect(() => {
    const handleUnauthorized = (event) => {
      const reason = event?.detail?.reason;
      if (reason === 'session_expired') {
        window._workplus_session_expired = true;
      }
      logout();
    };
    const handleRateLimited = (event) => {
      const msg = event?.detail?.message || 'Too many attempts. Try again later.';
      // Can't use toast here (outside React tree), so store for Login page to show
      window._workplus_rate_limit_msg = msg;
    };
    window.addEventListener('workplus:unauthorized', handleUnauthorized);
    window.addEventListener('workplus:rate_limited', handleRateLimited);
    return () => {
      window.removeEventListener('workplus:unauthorized', handleUnauthorized);
      window.removeEventListener('workplus:rate_limited', handleRateLimited);
    };
  }, [logout]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('workplus_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    if (userData.company_id) {
      await loadCompany();
    }
    
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await api.post('/auth/register', { 
      name, 
      email, 
      password,
      role: 'employee'
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('workplus_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const registerCompany = async (companyData) => {
    const response = await api.post('/companies/register', companyData);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('workplus_token', access_token);
    setToken(access_token);
    setUser(userData);
    
    await loadCompany();
    
    return userData;
  };

  const value = {
    user,
    company,
    token,
    loading,
    login,
    logout,
    register,
    registerCompany,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'admin',
    isHR: user?.role === 'hr',
    isController: user?.role === 'controller',
    isEmployee: user?.role === 'employee',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
